import { useState } from 'react';
import { KeyRound } from 'lucide-react';
import { appApi, setTokenApp, ErroApp, type ClienteApp } from './api';
import { CampoSenha } from './campos';

// "Trocar senha", dentro de ☰ → Configuracoes (02/09/2026).
//
// ⚠️ **Ate hoje a senha do AppCel era escolhida UMA vez**, no cadastro por convite:
// nao havia como troca-la nem como recupera-la.
//
// ⚠️ **Nao confundir com o "Esqueci minha senha" da tela de entrada.** Aquele e' o
// caminho de quem NAO sabe a senha, e depende da loja abrir uma janela de 2h. Este
// e' o de quem sabe e so quer outra — e nao depende de ninguem.
//
// ⚠️ **A senha atual e' pedida mesmo estando logado.** O app fica logado por 30
// dias renovaveis num celular que passa o dia destravado em cima do balcao; sem
// esse campo, quem pega o aparelho por um minuto tranca o dono pra fora da conta.
//
// ⚠️ **Gravar o token que volta nao e' detalhe**: a troca incrementa a
// `token_versao`, o que invalida o token guardado neste aparelho. Sem o
// `setTokenApp`, a pessoa trocaria a senha e cairia na tela de entrada no mesmo
// instante — parecendo que deu errado justamente quando deu certo.

const COBRE = '#b45309';
const CINZA = '#6b7280';
const BORDA = '#e7e5e4';

/** O mesmo minimo do cadastro e do servidor. Numero diferente aqui faria a tela
 *  recusar o que a porta de entrada aceita. */
const MINIMO = 6;

export function TrocarSenhaApp({ cliente, aoAtualizar, soLeitura = false }: {
  cliente: ClienteApp;
  aoAtualizar: (c: ClienteApp) => void;
  /** Modo ESPELHO ([DA-634]): o servidor recusa toda escrita, entao aqui a tela
   *  aparece explicando por que, em vez de oferecer o que vai ser negado. */
  soLeitura?: boolean;
}): React.ReactElement {
  const [aberto, setAberto] = useState(false);
  const [atual, setAtual] = useState('');
  const [nova, setNova] = useState('');
  const [repetida, setRepetida] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [pronto, setPronto] = useState(false);

  // ⚠️ Controle travado DIZ por que (`CLAUDE.md` raiz, § UI) — e o texto fica
  // VISIVEL embaixo do botao, nao escondido num `title`. Botao cinza e mudo e' beco
  // sem saida: a pessoa toca, nada acontece, e nao ha como adivinhar o que falta.
  const impedimento =
    !atual ? 'Digite a senha que você usa hoje.'
    : nova.length < MINIMO ? `A senha nova precisa de pelo menos ${MINIMO} caracteres.`
    : nova === atual ? 'A senha nova tem que ser diferente da atual.'
    : repetida !== nova ? 'A repetição não confere com a senha nova.'
    : null;

  function mexeu(): void {
    setPronto(false);
    setErro(null);
  }

  function fechar(): void {
    setAberto(false);
    setAtual('');
    setNova('');
    setRepetida('');
    setErro(null);
  }

  async function salvar(): Promise<void> {
    if (impedimento || ocupado || soLeitura) return;
    setOcupado(true);
    setErro(null);
    try {
      const s = await appApi.trocarSenha(atual, nova);
      // A ORDEM importa: token primeiro. Qualquer coisa que falhe depois deixa o
      // aparelho com a credencial VALIDA — invertido, um erro no meio deixaria a
      // tela com o token velho, ja morto, e a proxima chamada derrubaria a sessao.
      setTokenApp(s.token);
      aoAtualizar(s.cliente);
      fechar();
      setPronto(true);
    } catch (e) {
      setErro((e as ErroApp).message || 'Não consegui trocar a senha agora.');
    } finally {
      setOcupado(false);
    }
  }

  return (
    <div style={{ backgroundColor: '#fff', borderBottom: `1px solid ${BORDA}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px 16px 12px' }}>
        <KeyRound size={20} style={{ color: COBRE, flexShrink: 0 }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 16 }}>Trocar senha</div>
          <div style={{ fontSize: 13, color: CINZA, marginTop: 2 }}>
            {soLeitura
              ? 'Modo espelho — só para ver, nada é gravado.'
              : pronto
                ? 'Senha trocada. Use a nova da próxima vez que entrar.'
                : 'A senha que você digita para entrar no aplicativo'}
          </div>
        </div>
        {!aberto && (
          <button
            type="button"
            onClick={() => { mexeu(); setAberto(true); }}
            disabled={soLeitura}
            style={{
              height: 40, padding: '0 14px', borderRadius: 9, border: `1px solid ${COBRE}`,
              backgroundColor: '#fff', color: COBRE, fontSize: 14.5, fontWeight: 700,
              flexShrink: 0, opacity: soLeitura ? 0.5 : 1,
            }}
          >
            Trocar
          </button>
        )}
      </div>

      {aberto && (
        <div style={{ padding: '4px 16px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* `autoComplete` declarado do jeito que o cofre do celular entende — e'
              ele quem tem que lembrar senha, nunca nós ([`api.ts`]). */}
          <CampoSenha
            rotulo="Senha atual"
            valor={atual}
            aoMudar={(v) => { setAtual(v); mexeu(); }}
            autoComplete="current-password"
          />
          <CampoSenha
            rotulo="Nova senha"
            valor={nova}
            aoMudar={(v) => { setNova(v); mexeu(); }}
            autoComplete="new-password"
            placeholder={`Pelo menos ${MINIMO} caracteres`}
          />
          {/* Campo de criar senha tem confirmacao, e cada um com o SEU olho: quem
              confere se a repeticao bate precisa abrir uma e manter a outra
              escondida (`CLAUDE.md` raiz, § UI). */}
          <CampoSenha
            rotulo="Repita a nova senha"
            valor={repetida}
            aoMudar={(v) => { setRepetida(v); mexeu(); }}
            autoComplete="new-password"
          />

          {erro && (
            <div style={{ padding: 10, borderRadius: 9, backgroundColor: '#fef2f2', color: '#b91c1c', fontSize: 13, lineHeight: 1.45 }}>
              {erro}
            </div>
          )}

          <button
            type="button"
            onClick={() => void salvar()}
            disabled={!!impedimento || ocupado}
            style={{
              height: 50, borderRadius: 10, border: 'none', backgroundColor: COBRE,
              color: '#fff', fontSize: 16, fontWeight: 700,
              opacity: impedimento || ocupado ? 0.5 : 1,
            }}
          >
            {ocupado ? 'Salvando...' : 'Salvar nova senha'}
          </button>

          <div style={{ fontSize: 12.5, color: CINZA, lineHeight: 1.5, textAlign: 'center' }}>
            {/* O que a troca faz ALEM de trocar a senha. Quem tiver entrado noutro
                celular vai ser mandado pra tela de entrada la — dito antes, isso e'
                o esperado; descoberto depois, e' defeito. */}
            {impedimento ?? 'Você continua conectado neste aparelho. Se entrou em outro celular, lá vai pedir a senha nova.'}
          </div>

          <button
            type="button"
            onClick={fechar}
            style={{
              height: 42, borderRadius: 9, border: '1px solid #d6d3d1', backgroundColor: '#fff',
              color: '#78716c', fontSize: 14.5, fontWeight: 600,
            }}
          >
            Cancelar
          </button>
        </div>
      )}
    </div>
  );
}
